import type {
  AuditEntry,
  Role,
  CorrectionView,
  DashboardResponse,
  HistoryEntry,
  MeResponse,
  ProductView,
  UsageResponse,
  VialView,
} from '@medistock/contracts';

export interface ProductInput {
  name: string;
  brand: string;
  unit: string;
  precision: number;
  minStep: number;
  vialSize: number;
  quickOptions: number[];
  reorderLevel: number;
  active?: boolean;
}

export interface AdminUser {
  id: number;
  code: string;
  displayName: string;
  role: Role;
  active: boolean;
  createdAt: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)ms_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH';
  body?: unknown;
  idempotencyKey?: string;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  // Spec §19: never queue writes for later. If the tablet is offline the save
  // must fail visibly rather than look saved and deduct twice on reconnect.
  if (method !== 'GET' && typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError(0, 'OFFLINE', 'No connection. Nothing was saved — try again once you are back online.');
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken();
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach the server. Nothing was saved.');
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : {};

  if (!response.ok) {
    const err = payload as { error?: string; code?: string; field?: string };
    throw new ApiError(
      response.status,
      err.code ?? 'INTERNAL_ERROR',
      err.error ?? 'Something went wrong',
      err.field,
    );
  }
  return payload as T;
}

export const api = {
  staff: () => request<{ staff: { id: number; code: string; displayName: string }[] }>('/api/auth/staff'),
  login: (code: string, pin: string) =>
    request<{ user: MeResponse; csrfToken: string }>('/api/auth/login', {
      method: 'POST',
      body: { code, pin },
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<{ user: MeResponse }>('/api/me'),
  session: () => request<{ user: MeResponse | null }>('/api/session'),
  products: () => request<{ products: ProductView[] }>('/api/products'),
  usage: (body: { productId: number; qty: number; type: 'USE' | 'WASTE'; reason?: string | null }, key: string) =>
    request<UsageResponse>('/api/usage', { method: 'POST', body, idempotencyKey: key }),
  history: () => request<{ entries: HistoryEntry[] }>('/api/staff/history'),
  correction: (body: {
    eventId: number;
    requestType: 'EDIT' | 'CANCEL';
    requestedQty?: number;
    reason: string;
  }) => request<{ id: number }>('/api/corrections', { method: 'POST', body }),
  dashboard: () => request<DashboardResponse>('/api/manager/dashboard'),
  inventory: () => request<{ vials: VialView[] }>('/api/manager/inventory'),
  receive: (body: {
    productId: number;
    lot: string;
    expiry: string;
    totalQty: number;
    costTotalUsd: number;
  }) => request<{ vialId: number }>('/api/manager/receive', { method: 'POST', body }),
  open: (vialId: number) =>
    request<{ vialId: number }>('/api/manager/open', { method: 'POST', body: { vialId } }),
  count: (body: { vialId: number; actualQty: number; reason?: string }) =>
    request<{ expected: number; actual: number }>('/api/manager/count', { method: 'POST', body }),
  corrections: (status = 'PENDING') =>
    request<{ requests: CorrectionView[] }>(`/api/manager/corrections?status=${status}`),
  decide: (id: number, decision: 'APPROVE' | 'REJECT', note?: string) =>
    request<{ status: string }>(`/api/manager/corrections/${id}/decision`, {
      method: 'POST',
      body: { decision, note },
    }),
  audit: (limit = 200) => request<{ entries: AuditEntry[] }>(`/api/manager/audit?limit=${limit}`),
  adminProducts: () =>
    request<{ products: (ProductView & { id: number })[] }>('/api/admin/products'),
  createProduct: (body: ProductInput) =>
    request<{ id: number }>('/api/admin/products', { method: 'POST', body }),
  updateProduct: (id: number, body: ProductInput) =>
    request<{ ok: true }>(`/api/admin/products/${id}`, { method: 'PATCH', body }),
  adminUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),
  createUser: (body: { code: string; displayName: string; role: Role; pin: string }) =>
    request<{ id: number }>('/api/admin/users', { method: 'POST', body }),
  updateUser: (id: number, body: { active?: boolean; role?: Role; pin?: string }) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'PATCH', body }),
};

export function money(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(Math.round(cents));
  return `${sign}$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function localTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
